import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { applyServerErrors } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { authApi } from '../api';
import { forgotPasswordSchema, type ForgotPasswordValues } from '../schemas';
import { AuthLayout } from '../components/AuthLayout';

/**
 * Ask for a reset link.
 *
 * The confirmation deliberately does NOT say whether the address is
 * registered, because the endpoint deliberately does not either — telling
 * somebody "no account with that email" turns this form into a way to ask
 * which businesses use us, one address at a time. So the screen says what it
 * honestly knows: if there is an account, a link is on its way.
 *
 * That makes the sent state carry more weight than the form does. It repeats
 * the address back, since the commonest reason no mail arrives is a typo the
 * person cannot now see; it names the spam folder, which is the second
 * commonest; and it offers a way back to correct the address without a
 * reload.
 */
export function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);

  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const request = useMutation({
    mutationFn: (values: ForgotPasswordValues) => authApi.forgotPassword(values.email),
    onSuccess: (_data, values) => setSentTo(values.email),
    onError: (error) => {
      if (!applyServerErrors(error, form.setError as never)) {
        toast.error(error instanceof Error ? error.message : 'Could not send the link.');
      }
    },
  });

  if (sentTo !== null) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="If an account exists for that address, a reset link is on its way."
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 rounded-xl border border-border bg-sunken p-4">
            <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-good" strokeWidth={2} aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-medium">Sent to {sentTo}</p>
              <p className="mt-1 text-sm leading-relaxed text-ink-secondary">
                The link expires in an hour and can only be used once. If nothing arrives, check
                the spam folder before trying again.
              </p>
            </div>
          </div>

          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => {
              setSentTo(null);
              form.reset({ email: sentTo });
            }}
          >
            Use a different address
          </Button>

          <p className="text-center text-sm text-ink-secondary">
            <Link
              to="/login"
              className="font-semibold text-ink underline underline-offset-4 hover:text-brand"
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter the email you sign in with and we will send you a link."
      footer={
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-brand"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} aria-hidden />
          Back to sign in
        </Link>
      }
    >
      <form
        onSubmit={form.handleSubmit((values) => request.mutate(values))}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label="Email" htmlFor="email" error={form.formState.errors.email?.message} required>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            autoFocus
            aria-invalid={!!form.formState.errors.email}
            {...form.register('email')}
          />
        </Field>

        <Button type="submit" size="lg" loading={request.isPending} className="mt-1 w-full">
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
